import { useMutation } from '@apollo/client';
import { Alert, Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { gql } from '__generated__';
import { FC, useState } from 'react';

import Button from 'components/Button';
import { FormInput, FormPanel } from 'components/FormPanel';

const BULK_UPLOAD_TEMPLATE_URL = `${process.env.PUBLIC_URL}/bulk-upload-user-template.xlsx`;

const BULK_UPLOAD_USER_MUTATION = gql(`
  mutation BulkUploadUser($file: Upload!, $departmentId: ID!) {
    bulkUploadUser(file: $file, departmentId: $departmentId) {
      status
      totalRows
      importedRows
      skippedRows
      errorsByReason {
        field
        reason
        count
      }
    }
  }
`);

type BulkUploadResult = {
  totalRows: number;
  importedRows: number;
  skippedRows: number;
  errorsByReason: {
    field: string;
    reason: string;
    count: number;
  }[];
};

const getBulkUploadSummaryMessage = (result: BulkUploadResult) => {
  if (result.importedRows === 0) {
    return 'Bulk upload completed, but no rows were imported. Please fix the mandatory-field errors and upload again.';
  }

  if (result.skippedRows > 0) {
    return `Bulk upload completed. ${result.importedRows} of ${result.totalRows} rows imported. ${result.skippedRows} rows were skipped.`;
  }

  return `Bulk upload completed. ${result.importedRows} rows imported.`;
};

const BulkUploadUserDialogButton: FC<{ departmentId: string }> = ({ departmentId }) => {
  const [showDialog, toggleDialog] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null);

  const [bulkUploadUser, { loading, error }] = useMutation(BULK_UPLOAD_USER_MUTATION, {
    refetchQueries: ['DepartmentUsers'],
  });

  return (
    <>
      <Button
        onClick={() => {
          setUploadResult(null);
          toggleDialog(true);
        }}
      >
        Bulk Upload User
      </Button>
      <Dialog open={showDialog} onClose={() => toggleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Upload User</DialogTitle>
        <DialogContent sx={{ minHeight: '50vh' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Download the template, fill in employee details, then upload the Excel file.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <a href={BULK_UPLOAD_TEMPLATE_URL} download="bulk-upload-user-template.xlsx">
              <Button variant="text">Download Template</Button>
            </a>
          </Box>
          <FormPanel
            loading={loading}
            error={error}
            onCancel={() => toggleDialog(false)}
            onSubmit={data => {
              setUploadResult(null);
              bulkUploadUser({
                variables: {
                  file: data.file,
                  departmentId,
                },
              }).then(response => {
                if (response.data?.bulkUploadUser) {
                  setUploadResult(response.data.bulkUploadUser);
                }
              });
            }}
          >
            <FormInput fullWidth type="upload" fieldName="file" label="Document" defaultValue="" />
          </FormPanel>
          {uploadResult && (
            <Box sx={{ mt: 2 }}>
              <Alert
                severity={
                  uploadResult.importedRows === 0
                    ? 'error'
                    : uploadResult.skippedRows > 0
                    ? 'warning'
                    : 'success'
                }
              >
                {getBulkUploadSummaryMessage(uploadResult)}
              </Alert>
              {uploadResult.errorsByReason.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Skipped row summary
                  </Typography>
                  {uploadResult.errorsByReason.map(error => (
                    <Typography key={`${error.field}-${error.reason}`} variant="body2">
                      {error.reason} {error.field}: {error.count} rows
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkUploadUserDialogButton;
